import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useEffect } from 'react';
import { showResult } from 'infrastructure/hooks/utils';

import type { CcmStatuses, RpmStatuses } from 'infrastructure/enums';
import type {
  TStatuses,
  Value,
} from 'infrastructure/interfaces/i-rpm-patient-activity';

export interface GetPatientLatestReadingBody {
  startTime?: string;
  endTime?: string;
  patientGuids: string[];
  skipMACheck?: boolean;
  applySpecialFilter?: boolean;
  reviewed?: boolean;
}

export interface RpmPatientLatestReading {
  rpmStatus: RpmStatuses;
  ccmStatus: CcmStatuses;
  patientGuid: string;
  value: Value;
  createdAt: string;
  statuses: TStatuses;
}

const url = 'oneplatform/patients/readings/search-latest';

export const getPatientLatestReadingQueryKey = (
  body: GetPatientLatestReadingBody,
) => [url, body];

const queryFn = async (body: GetPatientLatestReadingBody) => {
  const { data, error } = await dataService.createList<
    RpmPatientLatestReading[]
  >(url, body);

  if (error) {
    return Promise.reject(error);
  }

  return data;
};

export const usePatientLatestReading = (body: GetPatientLatestReadingBody) => {
  const queryKey = getPatientLatestReadingQueryKey(body);

  const { data, isLoading, isFetching, error } = useQuery<
    RpmPatientLatestReading[] | undefined,
    string | undefined
  >({
    queryKey,
    queryFn: async () => queryFn(body),
    keepPreviousData: true,
    enabled: body.patientGuids.length > 0,
    staleTime: 0,
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  return {
    latestReadings: data ?? [],
    isLoading,
    isFetching,
  };
};
